// The API refused the credentials this browser holds (`invalid_device`: the
// device was signed out elsewhere, or its binding to the account is gone).
// From here on every call would answer as a guest while the page still shows
// the account, so the session ends here: the credentials, the profile and
// the user-bound storage go, and the person is told to sign in again.
//
// `installSessionGuard()` listens to the API wrapper's error events once per
// window; `sessionRefused()` is for callers that talk to the API on their own
// (the storefront's legacy contract) and see the error themselves.
import { getValue, setValue } from "aldoo-ra/Value"
import config from "aldoo-ra/Config"
import { clearUserLocalStorage } from "./logout"
import { clearCachedCredentials } from "aldoo-ra/API"

const persistKey = config.APP_NAME.toLowerCase().replace(" ", "-") + "-ck"
export const SESSION_REFUSED_EVENT = "aldoo:session-refused"
let installed = false
let refusing = false

/** Whether this browser holds a signed-in session at all. */
const signedIn = () => !!getValue("user-credentials", persistKey)?.auth_token

export function sessionRefused({ reason = "invalid_device" } = {}) {
    if (typeof window === "undefined" || refusing || !signedIn()) return false
    refusing = true
    try {
        setValue("user-credentials", null, persistKey)
        setValue("user-profile", null)
        clearCachedCredentials()
        clearUserLocalStorage()
        window.dispatchEvent(new CustomEvent(SESSION_REFUSED_EVENT, { detail: { reason } }))
    } finally {
        refusing = false
    }
    return true
}

export function installSessionGuard() {
    if (typeof window === "undefined" || installed) return
    installed = true
    window.addEventListener("API_RESPONSE_ERROR", (event) => {
        const { error, options } = event.detail || {}
        if (error !== "invalid_device") return
        // only a call that carried this session's token says something about it
        if (!options?.headers?.Authorization) return
        sessionRefused({ reason: error })
    })
}
